import { createStore, createEffect, createEvent, combine } from 'effector';
import { ApiUtils } from '@shared/utils';
import { UsersApi } from '@shared/api/rest';
import type { AddUserCardsRequest, DeleteUserCardsRequest, UsersRequest, UsersResponse } from '@shared/api/rest/users';
import { $notificationStore } from '../notificationStore';
import { type IQueryParams, RequestStrategyEnum } from '@shared/api/models';

const getCardUsersEffect = createEffect((params: IQueryParams<UsersRequest>) => UsersApi.getUsers(params.request, params?.signal));
const addUsersInCardEffect = createEffect((request: AddUserCardsRequest) => UsersApi.addUserCards(request));
const deleteUserInCardEffect = createEffect((params: IQueryParams<DeleteUserCardsRequest>) =>
  UsersApi.deleteUserCards(params.request, params?.signal),
);
const clearEvent = createEvent();

const $dataStore = createStore<UsersResponse[]>([])
  .on(getCardUsersEffect.done, (current, data) => data.result.data || current)
  .on(addUsersInCardEffect.done, (current, data) => (data.result.data ? [...current, data.result.data] : current))
  .on(deleteUserInCardEffect.done, (current, data) => {
    const userIds = (data.params.request as DeleteUserCardsRequest).userIds;
    return current.filter((user) => !userIds.includes(user.id));
  })
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getCardUsersEffect.pending, (_, data) => data)
  .on(getCardUsersEffect.fail, (_, data) => false)
  .on(getCardUsersEffect.done, (_, data) => false)
  .on(addUsersInCardEffect.pending, (_, data) => data)
  .on(addUsersInCardEffect.fail, (_, data) => false)
  .on(addUsersInCardEffect.done, (_, data) => false)
  .on(deleteUserInCardEffect.pending, (_, data) => data)
  .on(deleteUserInCardEffect.fail, (_, data) => false)
  .on(deleteUserInCardEffect.done, (_, data) => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getCardUsersEffect, () => null)
  .on(getCardUsersEffect.fail, (_, data) => data.error.message)
  .on(getCardUsersEffect.done, (_, data) => null)
  .on(addUsersInCardEffect, () => null)
  .on(addUsersInCardEffect.fail, (_, data) => data.error.message)
  .on(addUsersInCardEffect.done, (_, data) => null)
  .on(deleteUserInCardEffect, () => null)
  .on(deleteUserInCardEffect.fail, (_, data) => data.error.message)
  .on(deleteUserInCardEffect.done, (_, data) => null)
  .reset(clearEvent);

$errorStore.watch((x) => $notificationStore.events.setError(x));

export const $cardUsersStore = {
  events: {
    getCardUsers: ApiUtils.wrapperApi(getCardUsersEffect, RequestStrategyEnum.Debounce),
    addUsersInCard: addUsersInCardEffect,
    deleteUserInCard: ApiUtils.wrapperApi(deleteUserInCardEffect, RequestStrategyEnum.Debounce),
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
